/**
 * useAgentWebSocket — manages the WebSocket connection to the AI agent.
 *
 * Handles:
 * - Auth handshake on connect
 * - Automatic reconnection with exponential backoff
 * - Token streaming (assembles tokens into the current assistant message)
 * - Non-prose envelopes forwarded via onEnvelope callback
 * - Ping/pong keepalive every 30s
 */
import { useCallback, useEffect, useRef, useState } from "react"
import { AI_AGENT_BASE_URL } from "../config"
import {
  getLivisToken,
  getLivisLanguage,
  getLivisUserInfo,
} from "../helpers/storage"
import { resolveUserEmail } from "../services/aiAgent"
import { getCopilotUiContext } from "../components/AIAgent/context/copilotPageContext"
import { logger } from "../services/logger"
import { DOM_TRACKING_CONFIG } from "../services/logger/domTrackingConfig"

export type WsEnvelope = Record<string, any>

/** Connection lifecycle surfaced to the UI so the user is never left guessing. */
export type ConnectionState =
  | "connecting"
  | "online"
  | "reconnecting"
  | "offline"

export interface WorkBinding {
  bound_type: string
  bound_id: string
  bound_label: string
  source?: "bind" | "entity" // bind=explicit @mention (clearable), entity=inferred from context (informational)
}

export interface AgentMentionTarget {
  type: string
  id: string
  label: string
  workstation_id?: string
  station_id?: string
  usecase_id?: string
  part_id?: string
  ai_inspector_id?: string
  training_id?: string
  experiment_id?: string
}

export interface AgentWsCallbacks {
  onToken: (token: string) => void
  onStatus: (text: string) => void
  onEnvelope: (envelope: WsEnvelope) => void
  onDone: () => void
  onError: (detail: string) => void
  onConnected: () => void
  onDisconnected: () => void
  onPush?: (data: WsEnvelope) => void
  onBindOk?: (binding: WorkBinding) => void
  onMessagesTruncated?: (fromMessageId: string, removedCount: number) => void
  onMentionSuggestions?: (
    requestId: string,
    items: AgentMentionTarget[]
  ) => void
}

function buildWsUrl(): string {
  const base = (AI_AGENT_BASE_URL as string | undefined) || ""
  // Convert http(s):// to ws(s)://
  const wsBase = base
    .replace(/^https:\/\//, "wss://")
    .replace(/^http:\/\//, "ws://")
  return `${wsBase.replace(/\/$/, "")}/agent/ws`
}

function resolveUserContext(): { permissions: string; is_superuser: number } {
  try {
    const raw = getLivisUserInfo()
    if (!raw) return { permissions: "", is_superuser: 0 }
    const info: any = typeof raw === "string" ? JSON.parse(raw) : raw
    const perms = Array.isArray(info?.user_permissions)
      ? info.user_permissions.join(",")
      : ""
    return { permissions: perms, is_superuser: info?.is_superuser ? 1 : 0 }
  } catch {
    return { permissions: "", is_superuser: 0 }
  }
}

export function useAgentWebSocket(
  sessionId: string | null,
  callbacks: AgentWsCallbacks
) {
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pingTimer = useRef<ReturnType<typeof setInterval> | null>(null)
  const reconnectDelay = useRef(1000)
  const mountedRef = useRef(true)
  const callbacksRef = useRef(callbacks)
  callbacksRef.current = callbacks

  // C1: outgoing user payloads sent while the socket is down are queued and flushed on
  // reconnect, instead of being silently dropped (old behaviour: sendMessage returned false).
  // Each entry is tagged with the sessionId it was queued for — on flush we only send
  // payloads whose session still matches the current one (session-switch safety).
  const sendQueue = useRef<
    Array<{ sessionId: string | null; payload: Record<string, unknown> }>
  >([])
  const hasConnectedOnce = useRef(false)

  const [isAuthOk, setIsAuthOk] = useState(false)
  const [connectionState, setConnectionState] =
    useState<ConnectionState>("connecting")

  const flushQueue = useCallback(() => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    const queued = sendQueue.current
    sendQueue.current = []
    for (const { sessionId: qSid, payload } of queued) {
      // Drop payloads queued for a different session — user switched while offline.
      if (qSid !== sessionId) continue
      ws.send(JSON.stringify(payload))
    }
  }, [sessionId])

  const disconnect = useCallback(() => {
    if (pingTimer.current) clearInterval(pingTimer.current)
    if (reconnectTimer.current) clearTimeout(reconnectTimer.current)
    if (wsRef.current) {
      wsRef.current.onclose = null // prevent reconnect loop on intentional close
      wsRef.current.close()
      wsRef.current = null
    }
    setIsAuthOk(false)
  }, [])

  const connect = useCallback(() => {
    if (!sessionId || !mountedRef.current) return
    if (wsRef.current && wsRef.current.readyState <= WebSocket.OPEN) return

    setConnectionState(hasConnectedOnce.current ? "reconnecting" : "connecting")

    const url = buildWsUrl()
    const ws = new WebSocket(url)
    wsRef.current = ws

    ws.onopen = () => {
      if (!mountedRef.current) return
      reconnectDelay.current = 1000

      // Auth handshake
      const token = getLivisToken() || ""
      const { permissions, is_superuser } = resolveUserContext()
      ws.send(
        JSON.stringify({
          type: "auth",
          token: `Bearer ${token}`,
          session_id: sessionId,
          user_email: resolveUserEmail(),
          perms: permissions,
          is_superuser,
          industry: "none",
          language: getLivisLanguage() || "en",
          ui_context: getCopilotUiContext(),
          timezone_offset: new Date().getTimezoneOffset(),
        })
      )

      // Keepalive ping every 30s
      if (pingTimer.current) clearInterval(pingTimer.current)
      pingTimer.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN)
          ws.send(JSON.stringify({ type: "ping" }))
      }, 30_000)
    }

    ws.onmessage = (event) => {
      if (!mountedRef.current) return
      let data: Record<string, any>
      try {
        data = JSON.parse(event.data)
      } catch {
        return
      }

      const cb = callbacksRef.current
      switch (data.type) {
        case "auth_ok":
          setIsAuthOk(true)
          hasConnectedOnce.current = true
          setConnectionState("online")
          flushQueue() // resend anything the user submitted while offline
          cb.onConnected()
          if (DOM_TRACKING_CONFIG.webSocketEvents) {
            logger.action({ message: "WS_CONNECTED" })
          }
          break
        case "status":
        case "stream_status":
          cb.onStatus(data.text || "")
          break
        case "token":
          cb.onToken(data.text || "")
          break
        case "envelope":
          if (data.data) cb.onEnvelope(data.data)
          break
        case "done":
          cb.onDone()
          break
        case "error":
          cb.onError(data.detail || "unknown error")
          break
        case "pong":
          break
        case "push":
          if (data.data) cb.onPush?.(data.data)
          break
        case "bind_ok":
        case "context_sync":
          cb.onBindOk?.({
            bound_type: String(data.bound_type || ""),
            bound_id: String(data.bound_id || ""),
            bound_label: String(data.bound_label || ""),
            source:
              data.type === "bind_ok"
                ? "bind"
                : (data.source as "bind" | "entity") || "bind",
          })
          break
        case "messages_truncated":
          cb.onMessagesTruncated?.(
            String(data.from_message_id || ""),
            Number(data.removed_count) || 0
          )
          break
        case "mention_suggestions":
          cb.onMentionSuggestions?.(
            String(data.request_id || ""),
            Array.isArray(data.items) ? data.items : []
          )
          break
      }
    }

    ws.onclose = () => {
      if (!mountedRef.current) return
      if (pingTimer.current) clearInterval(pingTimer.current)
      setIsAuthOk(false)
      setConnectionState(
        typeof navigator !== "undefined" && navigator.onLine === false
          ? "offline"
          : "reconnecting"
      )
      callbacksRef.current.onDisconnected()

      // Exponential backoff reconnect (max 30s)
      const delay = Math.min(reconnectDelay.current, 30_000)
      reconnectDelay.current = Math.min(delay * 2, 30_000)
      if (DOM_TRACKING_CONFIG.webSocketEvents) {
        logger.action({ message: "WS_DISCONNECTED", reconnect_delay_ms: delay })
      }
      reconnectTimer.current = setTimeout(() => {
        if (mountedRef.current) connect()
      }, delay)
    }

    ws.onerror = () => {
      if (DOM_TRACKING_CONFIG.webSocketEvents) {
        logger.action({ message: "WS_ERROR" })
      }
      ws.close() // triggers onclose → reconnect
    }
  }, [sessionId, flushQueue])

  // Connect when sessionId changes
  useEffect(() => {
    mountedRef.current = true
    if (sessionId) connect()
    return () => {
      mountedRef.current = false
      disconnect()
    }
  }, [sessionId, connect, disconnect])

  const requestMentionSuggestions = useCallback(
    (query: string, requestId: string) => {
      const ws = wsRef.current
      if (!ws || ws.readyState !== WebSocket.OPEN || !isAuthOk) return false
      ws.send(
        JSON.stringify({
          type: "mention_suggest",
          query,
          request_id: requestId,
        })
      )
      return true
    },
    [isAuthOk]
  )

  const sendMessage = useCallback(
    (
      text: string,
      mode = "agent",
      options?: {
        regenerate?: boolean
        message_id?: string
        regenerate_from_message_id?: string
        regenerate_goal?: string
        mentions?: AgentMentionTarget[]
      }
    ) => {
      const payload: Record<string, unknown> = {
        type: "message",
        text,
        mode,
        ui_context: getCopilotUiContext(),
      }
      if (options?.mentions?.length) payload.mentions = options.mentions
      if (options?.regenerate) payload.regenerate = true
      if (options?.message_id) payload.message_id = options.message_id
      if (options?.regenerate_from_message_id) {
        payload.regenerate_from_message_id = options.regenerate_from_message_id
      }
      if (options?.regenerate_goal)
        payload.regenerate_goal = options.regenerate_goal
      const ws = wsRef.current
      if (!ws || ws.readyState !== WebSocket.OPEN || !isAuthOk) {
        sendQueue.current.push({ sessionId, payload })
        return true
      }
      ws.send(JSON.stringify(payload))
      return true
    },
    [isAuthOk]
  )

  const sendEvent = useCallback(
    (action: string, value?: any, markerId?: string) => {
      const ws = wsRef.current
      if (!ws || ws.readyState !== WebSocket.OPEN || !isAuthOk) return false
      ws.send(
        JSON.stringify({
          type: "event",
          action,
          value,
          marker_id: markerId ?? null,
          ui_context: getCopilotUiContext(),
        })
      )
      return true
    },
    [isAuthOk]
  )

  const sendSessionStart = useCallback(() => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN || !isAuthOk) return false
    ws.send(
      JSON.stringify({
        type: "session_start",
        ui_context: getCopilotUiContext(),
      })
    )
    return true
  }, [isAuthOk])

  const sendCancel = useCallback(() => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return false
    ws.send(JSON.stringify({ type: "cancel" }))
    return true
  }, [])

  const bindWork = useCallback(
    (bound_type: string, bound_id: string, bound_label?: string) => {
      const ws = wsRef.current
      if (!ws || ws.readyState !== WebSocket.OPEN || !isAuthOk) return false
      ws.send(
        JSON.stringify({
          type: "bind_work",
          bound_type,
          bound_id,
          bound_label: bound_label || "",
          ui_context: getCopilotUiContext(),
        })
      )
      return true
    },
    [isAuthOk]
  )

  const unbindWork = useCallback(() => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN || !isAuthOk) return false
    ws.send(
      JSON.stringify({ type: "unbind_work", ui_context: getCopilotUiContext() })
    )
    return true
  }, [isAuthOk])

  const sendEditMessage = useCallback(
    (messageId: string, text: string, mode = "agent") => {
      const payload = {
        type: "edit_message",
        message_id: messageId,
        text,
        mode,
        ui_context: getCopilotUiContext(),
      }
      const sock = wsRef.current
      if (!sock || sock.readyState !== WebSocket.OPEN || !isAuthOk) {
        sendQueue.current.push({ sessionId, payload })
        return true
      }
      sock.send(JSON.stringify(payload))
      return true
    },
    [isAuthOk]
  )

  return {
    isAuthOk,
    connectionState,
    requestMentionSuggestions,
    sendMessage,
    sendEditMessage,
    sendEvent,
    sendSessionStart,
    sendCancel,
    bindWork,
    unbindWork,
  }
}
